import { OpenPanel } from '@openpanel/web';
import { IS_SA, OPENPANEL_API_URL, OPENPANEL_CLIENT_ID } from '../config/appConfig';
import { InvestorType } from '../constants/investorType';
import { Roles } from '../constants/roles';

// Analytics must never break a user flow: every export is fire-and-forget and
// a silent no-op when REACT_APP_OPENPANEL_CLIENT_ID is not configured.
let op = null;

// Route transitions can remount a screen (e.g. declaration -> marketplace),
// which would double-fire its mount event. Dedupe applies ONLY to
// trackOpenPanelView (screen-view events); user-action events must never be
// deduped — two identical actions in quick succession are two real events.
const DEDUPE_WINDOW_MS = 2000;
let lastViewEventKey = null;
let lastViewEventAt = 0;

const getRegion = () => (IS_SA ? 'SA' : 'AE');
const getLanguage = () => (localStorage.getItem('i18nextLng') === 'ar' ? 'ar' : 'en');

// Docx user_type is investor | borrower; the JWT role for investors is 'Lender'.
const toUserType = (role) => {
	const roles = Array.isArray(role) ? role : [role];
	if (roles.includes(Roles.Investor)) {
		return 'investor';
	}
	if (roles.includes(Roles.Borrower)) {
		return 'borrower';
	}
	return null;
};

export const initOpenPanel = () => {
	if (!OPENPANEL_CLIENT_ID || op) {
		return;
	}
	try {
		op = new OpenPanel({
			clientId: OPENPANEL_CLIENT_ID,
			...(OPENPANEL_API_URL ? { apiUrl: OPENPANEL_API_URL } : {}),
			trackScreenViews: true,
			trackOutgoingLinks: true,
			trackAttributes: true,
		});
	} catch (error) {
		op = null;
		console.warn('OpenPanel init failed.', error);
	}
};

export const trackOpenPanel = (event, properties = {}) => {
	if (!op) {
		return;
	}
	try {
		op.track(event, { region: getRegion(), ...properties });
	} catch (error) {
		console.warn('OpenPanel track failed.', error);
	}
};

// For events fired from component-mount effects (screen views): identical
// event+props within the window count once, absorbing remount jitter.
export const trackOpenPanelView = (event, properties = {}) => {
	try {
		const eventKey = `${event}|${JSON.stringify(properties)}`;
		const now = Date.now();
		if (eventKey === lastViewEventKey && now - lastViewEventAt < DEDUPE_WINDOW_MS) {
			return;
		}
		lastViewEventKey = eventKey;
		lastViewEventAt = now;
	} catch (error) {
		console.warn('OpenPanel view-dedupe failed.', error);
	}
	trackOpenPanel(event, properties);
};

export const identifyOpenPanel = (user, traits = {}) => {
	if (!op || !user?.nameid) {
		return;
	}
	try {
		const userType = toUserType(user.role);
		// account_status is deliberately NOT derived from the JWT status: the app's
		// enum (InComplete/Pending/Compliance/...) is not the docx vocabulary
		// (pending/verified/active/suspended), and a JWT-derived value would
		// overwrite the 'verified' set at declaration on the next token refresh.
		// It is set explicitly at funnel points (signup -> pending, declaration -> verified).
		op.identify({
			profileId: user.nameid,
			...(user.email ? { email: user.email } : {}),
			properties: {
				region: getRegion(),
				language: getLanguage(),
				...(userType ? { user_type: userType } : {}),
				...traits,
			},
		});
	} catch (error) {
		console.warn('OpenPanel identify failed.', error);
	}
};

export const clearOpenPanel = () => {
	if (!op) {
		return;
	}
	try {
		op.clear();
	} catch (error) {
		console.warn('OpenPanel clear failed.', error);
	}
};

// Docx §4.1 defines tier as retail | professional | corporate;
// the app's CorporateRetail folds into corporate (SPEC.md Decision #13).
export const toOpenPanelTier = (investorType) => {
	switch (investorType) {
		case InvestorType.Retail:
			return 'retail';
		case InvestorType.Professional:
			return 'professional';
		case InvestorType.Corporate:
		case InvestorType.CorporateRetail:
			return 'corporate';
		default:
			return null;
	}
};
